import { Box, Typography } from "@mui/material";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { landingColors } from "../../landingColors";
import { flatSteps, groupStartIndex } from "../../data/buyerSellerStepsData";
import { useScrollLinkedIndex } from "../../hooks/useScrollLinkedIndex";
import AppScreenshot from "../AppScreenshot";
import StepsTabToggle from "./StepsTabToggle";

// Height of each step block. Tall enough that a step holds the pinned column
// for a beat before handing over, short enough that the section doesn't feel
// like it has stopped the page. Deliberately not a viewport unit: blocks must
// be taller than the band the observer watches, but sizing them off viewport
// height lets a short laptop window blow through all six steps in one flick.
const STEP_BLOCK_MIN_HEIGHT = 420;

// LandingNav's Toolbar is a fixed 68px and the AppBar is `position: sticky`,
// so it occludes the top of the viewport for this whole section. Everything
// pinned here has to stack below it.
const NAV_HEIGHT = 68;
// The tab bar pins directly under the nav and stays put for the length of the
// section, so a reader part-way through the seller steps can jump straight to
// the buyer ones without scrolling back. Fixed rather than measured because
// the phone column's offset and the observer's band both key off it, and a
// value that settles a frame late would shift both.
const TAB_BAR_HEIGHT = 72;
const PINNED_TOP = NAV_HEIGHT + TAB_BAR_HEIGHT;

// The screenshot column is a full-height flex box that centres the phone in
// what's left below the pinned header, rather than a `top: 50%` +
// `translateY(-50%)` element — that transform applies in normal flow too, so
// it yanks the phone up over the section heading for the whole stretch before
// it starts sticking.
const PINNED_COLUMN_MAX_HEIGHT = 760;
const PHONE_WIDTH = 280;

// Sticky positioning releases as soon as the containing column's bottom is
// reached, which without this lands mid-way through the final step — the
// phone starts sliding off the top while the last point is still the active
// one. Half the difference between the pinned column and a step block is the
// exact break-even point, so it needs slack on top: at precisely break-even
// the phone comes unstuck in the same frame the last step reaches centre
// (measured: released by 8px at the closest approach). A band's worth of
// extra trailing space buys the last step a settled moment before the
// section lets go and hands the page to whatever follows.
const STEPS_COLUMN_TAIL_SLACK = 96;
const STEPS_COLUMN_TAIL =
  (PINNED_COLUMN_MAX_HEIGHT - STEP_BLOCK_MIN_HEIGHT) / 2 +
  STEPS_COLUMN_TAIL_SLACK;

const StepBlock = ({ step, index, isActive, registerStep }) => (
  <Box
    ref={registerStep(index)}
    sx={{
      minHeight: STEP_BLOCK_MIN_HEIGHT,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      // Inactive steps dim rather than hide, so the section still reads as a
      // plain list of six points if scroll linking never engages.
      opacity: isActive ? 1 : 0.38,
      transition: "opacity 0.35s ease",
    }}
  >
    <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start" }}>
      <Typography
        aria-hidden
        sx={{
          fontWeight: 800,
          fontSize: 40,
          lineHeight: 1,
          color: isActive ? landingColors.primary : landingColors.border,
          transition: "color 0.35s ease",
        }}
      >
        {step.step}
      </Typography>
      <Box>
        <Typography
          component="h3"
          sx={{
            fontWeight: 700,
            fontSize: 26,
            lineHeight: 1.25,
            color: landingColors.textPrimary,
            m: 0,
          }}
        >
          {step.title}
        </Typography>
        <Typography
          sx={{
            color: landingColors.textSecondary,
            mt: 1.5,
            fontSize: 17,
            lineHeight: 1.6,
            maxWidth: 560,
          }}
        >
          {step.description}
        </Typography>
      </Box>
    </Box>
  </Box>
);

// The tab bar and the screenshot column both pin while the step copy scrolls
// past them, the screenshot cross-fading to whichever step is level with it.
// The copy occupies the scroll distance, so the pinned stretch is paid for
// with content rather than empty space.
const DesktopStepSequence = () => {
  const prefersReducedMotion = useReducedMotion();
  const { activeIndex, registerStep, scrollToIndex } = useScrollLinkedIndex(
    flatSteps.length,
    { reducedMotion: prefersReducedMotion, topOffset: PINNED_TOP },
  );

  const activeStep = flatSteps[activeIndex];

  return (
    <Box>
      <Box
        sx={{
          position: "sticky",
          top: `${NAV_HEIGHT}px`,
          zIndex: 2,
          height: TAB_BAR_HEIGHT,
          display: "flex",
          alignItems: "center",
          // Opaque, and bled out past SectionContainer's inline padding, so
          // step copy passes cleanly underneath with no visible seam.
          bgcolor: landingColors.background,
          mx: { xs: -3, md: -5 },
          px: { xs: 3, md: 5 },
        }}
      >
        <StepsTabToggle
          activeGroup={activeStep.groupKey}
          onSelect={(groupKey) => scrollToIndex(groupStartIndex[groupKey])}
          sx={{ width: "100%" }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <Box
          sx={{
            flex: `0 0 ${PHONE_WIDTH}px`,
            position: "sticky",
            top: `${PINNED_TOP}px`,
            height: `min(calc(100vh - ${PINNED_TOP}px), ${PINNED_COLUMN_MAX_HEIGHT}px)`,
            display: "flex",
            alignItems: "center",
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={activeStep.screenshot.src}
              style={{ width: "100%" }}
              initial={
                prefersReducedMotion
                  ? false
                  : { opacity: 0, y: 24, scale: 0.97 }
              }
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={
                prefersReducedMotion
                  ? undefined
                  : { opacity: 0, y: -24, scale: 0.97 }
              }
              transition={{
                duration: prefersReducedMotion ? 0 : 0.35,
                ease: "easeOut",
              }}
            >
              <AppScreenshot
                src={activeStep.screenshot.src}
                alt={activeStep.screenshot.alt}
                platform={activeStep.screenshot.platform}
                variant="phone"
              />
            </motion.div>
          </AnimatePresence>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, pb: `${STEPS_COLUMN_TAIL}px` }}>
          {flatSteps.map((step, index) => (
            <StepBlock
              key={`${step.groupKey}-${step.step}`}
              step={step}
              index={index}
              isActive={index === activeIndex}
              registerStep={registerStep}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default DesktopStepSequence;
